import type {
  CarImg,
  PaymentLink,
  PaymentOption,
  Price,
  RenewDetails,
  Schemes,
} from './RenewQuote.types';
import { defineStore } from 'pinia';

import { computed, ref, watch } from 'vue';

import { ProductCode } from '@/shared/constants/Policy';
import { RSA_ADDON_ID, UserType } from './RenewQuote.const';

export const useRenewQuoteStore = defineStore(
  'RenewQuote',
  () => {
    const quoteNumber = ref<string>('');
    const details = ref<RenewDetails>();
    const paymentLinks = ref<PaymentLink[]>();
    const carImg = ref<CarImg | null>(null);
    const schemes = ref<Schemes[]>([]);
    const price = ref<Price>({ total: 0, perMonth: 0 });
    const paymentOptions = ref<PaymentOption[]>([]);
    const typeQatarId = ref<'individual' | 'company'>();
    const promotionType = ref<string>();
    const productBonus = ref<number>(0);

    const activePaymentLink = computed(() =>
      paymentLinks.value?.find(el => el.payLinkType === 'REN'),
    );

    const isGlmSchemes = computed(() => schemes.value?.some(el => el.glmScheme));

    const isRsaQuote = computed(
      () =>
        promotionType.value === 'noClaimsTPL'
        && details.value?.oldProdCode === ProductCode.TPL
        && schemes.value.findIndex(
          item => item.addons.findIndex(el => el.id === RSA_ADDON_ID) !== -1,
        ) !== -1,
    );

    const selectedSchemeCode = ref<string>();
    const selectedScheme = computed(() =>
      schemes.value.find(el => el.schemeCode === selectedSchemeCode.value),
    );

    watch(selectedScheme, (newValue) => {
      price.value.total = newValue?.price || 0;
      price.value.perMonth = newValue?.monthlyPrice || 0;
    });

    const totalPriceCoversOfSelectedScheme = computed(() =>
      selectedScheme.value?.covers.reduce((accum, current) => accum + current.value, 0),
    );

    const selectedAddons = ref<Schemes['addons']>([]);
    const totalPriceSelectedAddons = computed(() =>
      selectedAddons.value.reduce((accum, current) => accum + current.value, 0),
    );

    watch(details, (newValue) => {
      typeQatarId.value = newValue?.qatarID.length === 11 ? UserType.INDIVIDUAL : UserType.COMPANY;
    });

    return {
      quoteNumber,
      details,
      schemes,
      paymentLinks,
      carImg,
      selectedSchemeCode,
      activePaymentLink,
      selectedScheme,
      selectedAddons,
      totalPriceCoversOfSelectedScheme,
      totalPriceSelectedAddons,
      price,
      paymentOptions,
      typeQatarId,
      isGlmSchemes,
      promotionType,
      isRsaQuote,
      productBonus,
    };
  },
  {
    persist: {
      key: 'car-renew-quote',
    },
  },
);
